import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { Segmented } from "antd";
import { PercentageOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toFixed } from "utils/helpers";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
  Legend,
} from "recharts";

const AccProfitChart = (props) => {
  const { strategy } = props;
  const [type, setType] = useState("accProfit");

  const data = useMemo(() => {
    if (!strategy?.transactions) return [];
    return strategy.transactions.slice().reverse();
  }, [strategy]);

  const dataNormalized = useMemo(() => {
    if (!data.length) return [];

    const initialBalance = strategy.initialBalance;

    return data.reduce((acc, item) => {
      if (acc.length === 0) {
        acc.push({
          closeDate: dayjs(strategy.created).format("YYYY-MM-DD"),
          displayDate: dayjs(strategy.created).format("MMM DD"),
          accProfit: 0,
          accProfitPercent: 0,
          buyHoldReturn: 0,
        });
      }

      const lastAcc = acc[acc.length - 1];
      const balance = lastAcc.accProfit + item.netProfit.value;
      const buyHold = (item.closeRate / strategy.firstClosePrice - 1) * 100;
      const accProfitPercent = toFixed((balance / initialBalance) * 100, 2);

      acc.push({
        closeDate: dayjs(item.closeDate).format("YYYY-MM-DD"),
        displayDate: dayjs(item.closeDate).format("MMM DD"),
        accProfit: toFixed(balance, 2),
        accProfitPercent,
        buyHoldReturn: toFixed(buyHold, 2),
      });

      return acc;
    }, []);
  }, [data, strategy]);

  const gradientOffset = () => {
    if (!dataNormalized.length) return 0.5;
    const dataMax = Math.max(...dataNormalized.map((i) => i.accProfit));
    const dataMin = Math.min(...dataNormalized.map((i) => i.accProfit));

    if (dataMax <= 0) {
      return 0;
    }
    if (dataMin >= 0) {
      return 1;
    }

    return dataMax / (dataMax - dataMin);
  };

  const off = dataNormalized.length > 0 ? gradientOffset() : 0.5;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      if (type === "accProfit") {
        const pnlValue = data.accProfit;
        const isPositive = pnlValue >= 0;

        return (
          <div className="p-3 border border-gray-600 rounded-lg bg-[#111827] shadow-lg">
            <p className="text-gray-300 text-sm">
              {dayjs(data.closeDate).format("MMM DD, YYYY")}
            </p>
            <p
              className={`font-semibold ${
                isPositive ? "text-green-400" : "text-red-400"
              }`}
            >
              Profit: ${(pnlValue || 0).toLocaleString()}
            </p>
          </div>
        );
      } else {
        return (
          <div className="p-3 border border-gray-600 rounded-lg bg-[#111827] shadow-lg">
            <p className="text-gray-300 text-sm">
              {dayjs(data.closeDate).format("MMM DD, YYYY")}
            </p>
            <p className="font-semibold text-blue-400">
              Strategy: {(data.accProfitPercent || 0).toLocaleString()}%
            </p>
            <p className="font-semibold text-orange-400">
              Buy & Hold: {data.buyHoldReturn}%
            </p>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <GenericCard
      title="Profit over time"
      maxHeight={1000}
      isCardOpenByDefault={false}
      className="table-card [&>.content]:p-0 [&>.content]:overflow-hidden [&>.content]:h-full"
    >
      <div className="h-full relative">
        <div className="p-4 pb-0 absolute top-[15px] left-[75px] z-10">
          <Segmented
            options={[
              {
                label: "USD",
                value: "accProfit",
              },
              {
                label: "Profit",
                icon: <PercentageOutlined />,
                value: "buyHold",
              },
            ]}
            value={type}
            onChange={setType}
          />
        </div>

        <ResponsiveContainer
          width="100%"
          height="100%"
          className="min-h-[600px] max-h-[700px]"
        >
          {type === "accProfit" ? (
            <AreaChart
              data={dataNormalized}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 10,
              }}
            >
              <defs>
                <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={off} stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset={off} stopColor="#ef4444" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="2 2"
                stroke="#374151"
                strokeOpacity={1}
              />
              <XAxis
                dataKey="displayDate"
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: "10px" }}
                iconType="line"
                formatter={(value) => (
                  <span style={{ color: "#9ca3af" }}>Accumulated Profit</span>
                )}
              />
              <ReferenceLine
                y={0}
                stroke="#6b7280"
                strokeDasharray="2 2"
                strokeOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="accProfit"
                stroke="#ffffff"
                strokeOpacity={0.4}
                strokeWidth={1.5}
                fill="url(#splitColor)"
                isAnimationActive={false}
                dot={false}
                activeDot={{
                  r: 6,
                  stroke: "#10b981",
                  strokeWidth: 2,
                  fill: "#10b981",
                }}
                name="Accumulated Profit"
              />
              <Brush
                dataKey="displayDate"
                height={25}
                stroke="#8884d8"
                fill="#374151"
                tickFormatter={(value) => value}
              />
            </AreaChart>
          ) : (
            <LineChart
              data={dataNormalized}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 10,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#374151"
                strokeOpacity={1}
              />
              <XAxis
                dataKey="displayDate"
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: "5px" }}
                iconType="line"
                formatter={(value, entry) => (
                  <span style={{ color: entry.color }}>
                    {value === "accProfitPercent"
                      ? "Strategy Performance"
                      : "Buy & Hold"}
                  </span>
                )}
              />
              <ReferenceLine
                y={0}
                stroke="#6b7280"
                strokeDasharray="2 2"
                strokeOpacity={0.6}
              />
              <Line
                type="monotone"
                dataKey="accProfitPercent"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 6,
                  stroke: "#3b82f6",
                  strokeWidth: 2,
                  fill: "#3b82f6",
                }}
                name="Strategy Performance"
              />
              <Line
                type="monotone"
                dataKey="buyHoldReturn"
                stroke="#f97316"
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 6,
                  stroke: "#f97316",
                  strokeWidth: 2,
                  fill: "#f97316",
                }}
                name="Buy & Hold"
              />
              <Brush
                dataKey="displayDate"
                height={30}
                stroke="#8884d8"
                fill="#374151"
                tickFormatter={(value) => value}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </GenericCard>
  );
};

AccProfitChart.propTypes = {
  strategy: PropTypes.shape({
    initialBalance: PropTypes.number.isRequired,
    firstClosePrice: PropTypes.number.isRequired,
    created: PropTypes.string.isRequired,
    transactions: PropTypes.arrayOf(
      PropTypes.shape({
        closeDate: PropTypes.string.isRequired,
        netProfit: PropTypes.shape({
          value: PropTypes.number.isRequired,
        }).isRequired,
        closeRate: PropTypes.number.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default AccProfitChart;
